import { Injectable, inject } from '@angular/core';

import type { Observable } from 'rxjs';
import { forkJoin, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';

import { IntegrationsApiService } from '@core/integrations-http-legacy';

import type { FormConfig, FormConfigCreateParams, FormConfigInputs } from '../../../types';

@Injectable({ providedIn: 'root' })
export class FormConfigHttpClient {
  private readonly httpClient = inject(IntegrationsApiService);

  public fetch(integrationKind: string): Observable<FormConfig[]> {
    return this.httpClient
      .get(`field-mapping/i/${integrationKind}/form-configs`)
      .pipe(
        switchMap(({ formConfigs }: { formConfigs: FormConfig[] }) =>
          this.preloadInputs(integrationKind, formConfigs),
        ),
      );
  }

  public createFormConfigs(
    integrationKind: string,
    params: FormConfigCreateParams[],
  ): Observable<FormConfig[]> {
    return this.httpClient
      .post(`/field-mapping/i/${integrationKind}/form-configs`, {
        formConfigs: params,
      })
      .pipe(
        switchMap(({ formConfigs }: { formConfigs: FormConfig[] }) =>
          this.preloadInputs(integrationKind, formConfigs),
        ),
      );
  }

  public deleteFormConfig(integrationKind: string, formConfigId: number): Observable<any> {
    return this.httpClient.delete(
      `/field-mapping/i/${integrationKind}/form-configs/${formConfigId}`,
    );
  }

  private preloadInputs(
    integrationKind: string,
    formConfigs: FormConfig[],
  ): Observable<FormConfig[]> {
    if (!formConfigs.length) return of([]);

    return forkJoin(
      formConfigs.map((formConfig: FormConfig) =>
        this.httpClient
          .get(`field-mapping/i/${integrationKind}/form-configs/${formConfig.id}`)
          .pipe(
            catchError(errorResponse => {
              const errors = errorResponse?.error?.error || [];

              return of({ errors });
            }),
            map(
              ({
                formConfig: formConfigInputs,
                errors,
              }: {
                formConfig: FormConfigInputs;
                errors?: string[];
              }) => ({
                ...formConfig,
                ...formConfigInputs,
                errors,
              }),
            ),
          ),
      ),
    );
  }
}
